/**
 * Campo de formulário: rótulo + controle (input/select/textarea) + dica/erro.
 * Rótulo sempre em `<label for>` real. Erro liga `aria-invalid` + `aria-describedby`.
 * Uso:
 *   <Field label="Razão social" value={v} onChange={...} error={fields?.razaoSocial} />
 *   <Field as="select" label="Estado" value={uf} onChange={...}><option .../></Field>
 *   <Field as="textarea" label="Observações" value={obs} onChange={...} />
 */
import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

interface FieldChrome {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Classe no wrapper `.field` (não no controle). */
  wrapperClassName?: string;
}

type FieldInputProps = FieldChrome &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & { as?: 'input'; id?: string };

type FieldSelectProps = FieldChrome &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & { as: 'select'; id?: string; children: ReactNode };

type FieldTextareaProps = FieldChrome &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & { as: 'textarea'; id?: string };

export type FieldProps = FieldInputProps | FieldSelectProps | FieldTextareaProps;

export function Field(props: FieldProps) {
  const autoId = useId();
  const id = props.id ?? autoId;
  const { label, hint, error, required, wrapperClassName } = props;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  let control: ReactNode;
  if (props.as === 'select') {
    const { as, label: _label, hint: _hint, error: _error, required: _required, wrapperClassName: _wrap, className, children, ...rest } =
      props;
    control = (
      <select
        id={id}
        className={['select', className].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        aria-required={required}
        required={required}
        {...rest}
      >
        {children}
      </select>
    );
  } else if (props.as === 'textarea') {
    const { as, label: _label, hint: _hint, error: _error, required: _required, wrapperClassName: _wrap, className, ...rest } =
      props;
    control = (
      <textarea
        id={id}
        className={['textarea', className].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        aria-required={required}
        required={required}
        {...rest}
      />
    );
  } else {
    const { as, label: _label, hint: _hint, error: _error, required: _required, wrapperClassName: _wrap, className, ...rest } =
      props;
    control = (
      <input
        id={id}
        className={['input', className].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        aria-required={required}
        required={required}
        {...rest}
      />
    );
  }

  return (
    <div className={['field', wrapperClassName].filter(Boolean).join(' ')}>
      <label className="field-label" htmlFor={id}>
        {label}
        {required ? ' *' : ''}
      </label>
      {control}
      {hint && !error ? (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
